import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Lock, Mail, Loader2, Eye, EyeOff, ArrowRight, User } from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuth } from '@/lib/auth'
import { api } from '@/lib/api'

interface UsuarioLogin { id: number; nome: string }

export default function LoginPage() {
  const nav = useNavigate()
  const { login, loginById } = useAuth()
  const [loading, setLoading] = useState(false)
  const [email, setEmail] = useState('')
  const [senha, setSenha] = useState('')
  const [showPw, setShowPw] = useState(false)
  // Login por nome (dropdown) é o padrão; e-mail fica como reserva (super admin/exceções)
  const [modoEmail, setModoEmail] = useState(false)
  const [usuarios, setUsuarios] = useState<UsuarioLogin[]>([])
  const [usuarioId, setUsuarioId] = useState<number | ''>('')

  useEffect(() => {
    api.get('/auth/usuarios-login')
      .then(({ data }) => {
        setUsuarios(data)
        if (data.length === 1) setUsuarioId(data[0].id)
      })
      .catch(() => setModoEmail(true))  // sem lista → cai pro e-mail
  }, [])

  async function onLogin(e: React.FormEvent) {
    e.preventDefault()
    if (!modoEmail && !usuarioId) { toast.error('Selecione o usuário'); return }
    setLoading(true)
    try {
      if (modoEmail) await login(email, senha)
      else await loginById(usuarioId as number, senha)
      nav('/')
      toast.success('Bem-vindo ao VELORE ERP')
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Usuário ou senha inválidos'
      toast.error(typeof msg === 'string' ? msg : 'Falha no login')
    } finally { setLoading(false) }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-ink-950 relative overflow-hidden px-4 py-10">
      {/* Fundo */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1/4 -left-20 w-72 sm:w-96 h-72 sm:h-96 bg-brand-600 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-20 w-72 sm:w-96 h-72 sm:h-96 bg-brand-800 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-sm">
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-2xl shadow-black/40 ring-1 ring-white/10 overflow-hidden">
          {/* Cabeçalho com logo */}
          <div className="flex flex-col items-center px-8 pt-9 pb-6 border-b border-ink-100">
            <div className="w-20 h-20 rounded-2xl shadow-lg shadow-brand-900/20 ring-1 ring-ink-200/60 overflow-hidden">
              <svg viewBox="0 0 64 64" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="veloreGrad" x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse">
                    <stop offset="0" stopColor="#34d399" />
                    <stop offset="0.55" stopColor="#2dd4bf" />
                    <stop offset="1" stopColor="#38bdf8" />
                  </linearGradient>
                </defs>
                <rect width="64" height="64" rx="17" fill="url(#veloreGrad)" />
                <rect x="1.5" y="1.5" width="61" height="61" rx="15.5" fill="none" stroke="#ffffff" strokeOpacity="0.18" />
                <path d="M13 16 L24 16 L32 34 L40 16 L51 16 L32 48 Z" fill="#ffffff" />
                <path d="M13 16 L24 16 L32 34 L32 48 Z" fill="#063b32" fillOpacity="0.10" />
              </svg>
            </div>
            <h1 className="mt-4 text-2xl font-extrabold tracking-tight" style={{ backgroundImage: 'linear-gradient(120deg,#34d399,#2dd4bf,#38bdf8)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>VELORE ERP</h1>
            <div className="mt-2 flex items-center gap-2">
              <span className="h-px w-6 bg-ink-200" />
              <span className="text-[10px] font-semibold tracking-[0.2em] text-brand-600">GESTÃO EMPRESARIAL</span>
              <span className="h-px w-6 bg-ink-200" />
            </div>
            <p className="mt-3 text-sm text-ink-500">Entre na sua conta para continuar</p>
          </div>

          {/* Formulário */}
          <form onSubmit={onLogin} className="px-8 py-7 space-y-5">
            {modoEmail ? (
              <div>
                <label className="label">E-mail</label>
                <div className="relative">
                  <Mail size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none" />
                  <input
                    type="email" required autoFocus autoComplete="email"
                    value={email} onChange={e => setEmail(e.target.value)}
                    className="input pl-10" placeholder="voce@empresa.com"
                  />
                </div>
              </div>
            ) : (
              <div>
                <label className="label">Usuário</label>
                <div className="relative">
                  <User size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none" />
                  <select
                    required autoFocus value={usuarioId}
                    onChange={e => setUsuarioId(e.target.value ? Number(e.target.value) : '')}
                    className="input pl-10 bg-white appearance-none"
                  >
                    <option value="">Selecione seu nome…</option>
                    {usuarios.map(u => (
                      <option key={u.id} value={u.id}>{u.nome}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            <div>
              <label className="label">Senha</label>
              <div className="relative">
                <Lock size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none" />
                <input
                  type={showPw ? 'text' : 'password'} required autoComplete="current-password"
                  value={senha} onChange={e => setSenha(e.target.value)}
                  className="input pl-10 pr-10" placeholder="••••••••"
                />
                <button
                  type="button" tabIndex={-1} onClick={() => setShowPw(v => !v)}
                  aria-label={showPw ? 'Ocultar senha' : 'Mostrar senha'}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-ink-400 hover:text-ink-600 rounded-md transition-colors"
                >
                  {showPw ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
            </div>

            <button
              type="submit" disabled={loading}
              className="group w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-brand-600 hover:bg-brand-700 active:bg-brand-800 text-white text-sm font-semibold shadow-sm shadow-brand-900/20 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading
                ? <><Loader2 size={17} className="animate-spin" /> Entrando...</>
                : <>Entrar <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" /></>}
            </button>

            <button
              type="button"
              onClick={() => { setModoEmail(v => !v); setEmail(''); setUsuarioId('') }}
              className="w-full text-center text-xs text-ink-500 hover:text-brand-600 transition-colors"
            >
              {modoEmail ? '← Escolher pelo nome' : 'Entrar com e-mail'}
            </button>
          </form>
        </div>

        {/* Rodapé */}
        <div className="text-center text-xs text-ink-500 mt-6 space-y-0.5">
          <div>VELORE ERP · v0.2.0</div>
          <div className="text-ink-600">Desenvolvido por <span className="font-medium text-ink-400">DN Tecnologia</span></div>
        </div>
      </div>
    </div>
  )
}
